import { useRef, type RefObject } from 'react';
import { createLazyModuleLoader, useLazyModule } from '@bliks-io/ui';
import { useLatchedTrue, usePreviousValue } from '@/hooks/renderPhaseState';
import {
  ProcessMapDialogFrame,
  ProcessMapErrorBody,
  ProcessMapLoadingBody
} from './ProcessMapDialogFrame';
import type { ProcessMapBodyProps, ProcessMapDialogProps } from './ProcessMapBody';

/**
 * The map, behind its chunk — and the only entry point a host uses.
 *
 * Every host renders this unconditionally beside its own dialog, so it has to
 * cost nothing until someone asks for a map: the chunk is not requested until
 * `open` first turns true. After that the chunk state stays mounted — a second
 * open must not pay for the module again — and the loader memoises the download
 * across the whole session anyway.
 *
 * The focus capture is owned HERE rather than deeper down because this is the
 * only component in the stack that is already mounted while the map is shut,
 * and so the only one that can observe `open` flipping false→true.
 */

type ProcessMapBodyComponent = (typeof import('./processMapDialogChunk'))['ProcessMapBody'];

const loadProcessMapBody = createLazyModuleLoader<ProcessMapBodyComponent>(() =>
  import('./processMapDialogChunk').then((module) => module.ProcessMapBody)
);

/**
 * The element that was focused when `open` last turned true.
 *
 * Captured during the render that observes the flip, because that is the last
 * moment the opener is still `document.activeElement` — an effect runs after
 * Radix has already moved focus into the dialog.
 *
 * Lives HERE, beside its only legitimate caller, because it must be called
 * from a component that is already mounted while the map is shut. A component
 * that first mounts with `open` already true observes no flip and hands back a
 * ref that is null forever — which is exactly what happened when the frame
 * owned this hook and only appeared on activation.
 */
function useOpenerFocusRestore(open: boolean): RefObject<HTMLElement | null> {
  const opener = useRef<HTMLElement | null>(null);
  const wasOpen = usePreviousValue(open);

  if (open && !wasOpen) opener.current = document.activeElement as HTMLElement | null;

  return opener;
}

export function LazyProcessMapDialog({ open, onOpenChange, ...body }: ProcessMapDialogProps) {
  const openerRef = useOpenerFocusRestore(open);
  const activated = useLatchedTrue(open);

  if (!activated) return null;
  return (
    <ActivatedProcessMapDialog
      open={open}
      onOpenChange={onOpenChange}
      openerRef={openerRef}
      body={body}
    />
  );
}

function ActivatedProcessMapDialog({
  open,
  onOpenChange,
  openerRef,
  body
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  openerRef: RefObject<HTMLElement | null>;
  body: ProcessMapBodyProps;
}) {
  const chunk = useLazyModule(loadProcessMapBody, 'Process map chunk');

  // ONE frame, whatever the chunk is doing — the ordering rule in
  // `ProcessMapDialogFrame`. Rendering nothing while the module arrives would
  // be the dead-button defect the data-loading state exists to avoid, and it
  // makes a retry survivable: `retry` returns the chunk to `pending`, which is
  // a state of this same frame rather than the absence of one.
  function renderBody() {
    if (chunk.status === 'failed') return <ProcessMapErrorBody onRetry={chunk.retry} />;
    if (chunk.status !== 'ready') return <ProcessMapLoadingBody />;
    const MapBody = chunk.module;
    return <MapBody {...body} />;
  }

  return (
    <ProcessMapDialogFrame open={open} onOpenChange={onOpenChange} openerRef={openerRef}>
      {renderBody()}
    </ProcessMapDialogFrame>
  );
}
